/**
 * The list numbering of a document, read from `word/numbering.xml` and
 * `word/styles.xml`, and the label Word paints in front of each numbered
 * paragraph: "17.6", "(b)", "iv.", "•".
 *
 * A paragraph does not carry its number. It carries a reference — a list
 * instance (`w:numId`) and a level (`w:ilvl`), on its own `w:pPr/w:numPr` or
 * inherited from its paragraph style — and the number is the count of
 * paragraphs at that level of that instance since the level last restarted.
 * The instance points at an abstract definition (`w:abstractNum`) that says,
 * per level, where counting starts (`w:start`), how the count is written
 * (`w:numFmt`) and how the label is assembled from the counts of this level and
 * the levels above it (`w:lvlText`, with `%1`…`%9` placeholders); an instance
 * may override a level's start or its whole definition (`w:lvlOverride`).
 *
 * The walk is the same for every list: one pass over the body paragraphs in
 * order, counters per instance and level, a deeper level restarting when a
 * shallower one ticks unless its `w:lvlRestart` says otherwise, a paragraph
 * whose mark is a tracked deletion painted with the count as it stands (it
 * joins the next paragraph once the change is accepted), and every
 * placeholder rendered through the same table-driven formatter, so a decimal
 * legal list, a lettered sub-list, a Roman schedule and a bulleted list are
 * one code path with different table rows.
 *
 * The label is reported beside the paragraph, in its own field; it is never
 * an id and never part of the text the engine edits.
 */
import { flatOpcPartXml, parseXmlDocumentOrNull } from "./flat-opc-parts";
import { NUMBERING_PART_NAME, STYLES_PART_NAME, WORDPROCESSINGML_NS, elementNameWithoutPrefix } from "./ooxml-names";
import { formatListNumber, isLiteralNumberFormat } from "../text/list-number-format";

/** The two parts list labels are computed from, as strings, or null when the file lacks them. */
export interface NumberingParts {
    numberingXml: string | null;
    stylesXml: string | null;
}

/** One `w:lvl`: how a level of a list counts and how its label is written. */
interface ListLevelDefinition {
    /** `w:start`: the count the level begins with, and returns to when it restarts. Zero when the file omits it, as the schema says. */
    startValue: number;
    /** `w:numFmt`: the ST_NumberFormat name the count is rendered in; `decimal` when omitted. */
    numberFormat: string;
    /** `w:lvlText`: the label pattern, `%N` standing for the count of level N (one-based); empty when omitted or `w:null`. */
    levelText: string;
    /**
     * `w:lvlRestart`: the one-based level whose use restarts this level.
     *
     * CASES
     *   - null -> omitted: this level restarts whenever any shallower level is used
     *   - 0    -> this level never restarts
     *   - n    -> this level restarts when level n or a shallower one is used
     */
    restartsAfterLevel: number | null;
    /** `w:isLgl`: legal numbering, every placeholder rendered decimal whatever its level's format ("1.1.2" in a lettered list). */
    isLegal: boolean;
    /** `w:pStyle` on the level: paragraphs of this style take this level when their style names only the instance. */
    linkedParagraphStyleId: string | null;
}

/** One `w:abstractNum`: the level definitions, or a link to the numbering style that holds them. */
interface AbstractNumberingDefinition {
    levelsByIndex: ReadonlyMap<number, ListLevelDefinition>;
    /** `w:numStyleLink`: this definition is a stub; the levels live under the instance the named numbering style points at. */
    numberingStyleLink: string | null;
}

/** One `w:lvlOverride` of an instance: a new start for the level, a whole new level definition, or both. */
interface ListLevelOverride {
    startOverride: number | null;
    level: ListLevelDefinition | null;
}

/** One `w:num`: a list instance the paragraphs reference. */
interface NumberingInstance {
    abstractNumberingId: string;
    overridesByLevel: ReadonlyMap<number, ListLevelOverride>;
}

/** What a `w:style` says about numbering: its own `w:numPr`, and the style it is based on. */
interface StyleNumbering {
    numberingId: string | null;
    level: number | null;
    basedOnStyleId: string | null;
}

/** A paragraph's resolved reference: which instance counts it, at which level. */
interface NumberingReference {
    numberingId: string;
    level: number;
}

/** The counter of one level of one instance during the walk. */
interface LevelCounter {
    /** The count the label shows; the start value until the level is first used, then one more per use. */
    value: number;
    /** WHEN false the level has not been used since it started or last restarted, so its next use shows the start value instead of incrementing. */
    used: boolean;
}

/** The level a resolved reference lands on, with the start the instance gives it. */
interface EffectiveLevel {
    definition: ListLevelDefinition;
    startValue: number;
}

/** Word supports nine list levels, `w:ilvl` 0 to 8. */
const LIST_LEVEL_COUNT = 9;
/** A style chain or numbering-style link longer than this is a cycle in a broken file; stop rather than loop. */
const MAX_LINK_DEPTH = 20;
/** `%N` in `w:lvlText`: the count of level N, one-based. */
const LEVEL_PLACEHOLDER = /%([1-9])/g;

const elementChildren = (parent: Element, localName: string): Element[] =>
    Array.from(parent.childNodes).filter((node): node is Element => node.nodeType === 1 && elementNameWithoutPrefix(node as Element) === localName);

const firstElementChild = (parent: Element, localName: string): Element | null => elementChildren(parent, localName)[0] ?? null;

const attributeValue = (element: Element | null, attributeName: string): string | null =>
    element === null ? null : (element.getAttributeNS(WORDPROCESSINGML_NS, attributeName) ?? element.getAttribute(`w:${attributeName}`));

/**
 * `w:val` of the named child, parsed as an integer.
 *
 * CASES
 *   - the child is absent or its value is not a number -> null
 *   - otherwise                                        -> the integer
 */
const integerChildValue = (parent: Element, localName: string): number | null => {
    const value = attributeValue(firstElementChild(parent, localName), "val");
    if (value === null) return null;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
};

export class OoxmlNumbering {
    private constructor(
        private readonly abstractDefinitionsById: ReadonlyMap<string, AbstractNumberingDefinition>,
        private readonly instancesById: ReadonlyMap<string, NumberingInstance>,
        private readonly stylesById: ReadonlyMap<string, StyleNumbering>,
    ) {}

    /** A document with no numbering part, or one whose parts were not available to the caller: every label is null. */
    static none(): OoxmlNumbering {
        return new OoxmlNumbering(new Map(), new Map(), new Map());
    }

    /**
     * Reads the numbering and styles parts of an unpacked `.docx`.
     *
     * CASES
     *   - no numbering part, or one that does not parse -> no list is defined; every label null
     *   - no styles part, or one that does not parse    -> lists are read, style-inherited numbering is not
     */
    static fromParts(parts: NumberingParts): OoxmlNumbering {
        const numberingDocument = parts.numberingXml === null ? null : parseXmlDocumentOrNull(parts.numberingXml);
        if (numberingDocument === null) return OoxmlNumbering.none();
        const stylesDocument = parts.stylesXml === null ? null : parseXmlDocumentOrNull(parts.stylesXml);
        return new OoxmlNumbering(
            OoxmlNumbering.abstractDefinitionsOf(numberingDocument),
            OoxmlNumbering.instancesOf(numberingDocument),
            stylesDocument === null ? new Map() : OoxmlNumbering.stylesOf(stylesDocument),
        );
    }

    /**
     * Reads the two parts out of a flat OPC `pkg:package`; a bare `w:document`
     * or a paragraph fragment has no parts and defines no list.
     */
    static fromPackage(packageXml: string): OoxmlNumbering {
        const packageDocument = parseXmlDocumentOrNull(packageXml);
        /**
         * WHEN the markup does not parse as a whole document
         *   CONSEQUENCE
         *     it is a fragment; fragments carry no numbering part.
         */
        if (packageDocument === null) return OoxmlNumbering.none();
        return OoxmlNumbering.fromParts({
            numberingXml: flatOpcPartXml(packageDocument, NUMBERING_PART_NAME),
            stylesXml: flatOpcPartXml(packageDocument, STYLES_PART_NAME),
        });
    }

    /**
     * The label Word paints before each of `paragraphs`, in their order, or
     * null for a paragraph that is not numbered. `paragraphs` must be the
     * body paragraphs in document order: the counts depend on what came
     * before.
     *
     * EXAMPLE
     *   instance 7: level 0 "%1." decimal from 1, level 1 "%1.%2" decimal from 1
     *   paragraphs at levels 0, 1, 1, 0, 1 -> "1.", "1.1", "1.2", "2.", "2.1"
     */
    labelsByParagraph(paragraphs: readonly Element[]): (string | null)[] {
        const countersByInstance = new Map<string, LevelCounter[]>();
        return paragraphs.map((paragraph) => {
            const reference = this.numberingReferenceOf(paragraph);
            /**
             * WHEN the paragraph references no list, or one the numbering part does not define
             *   CONSEQUENCE
             *     no label; the paragraph takes part in no count.
             */
            if (reference === null) return null;
            const instance = this.instancesById.get(reference.numberingId);
            if (instance === undefined) return null;
            const levels = this.effectiveLevelsOf(instance);
            /**
             * WHEN `w:ilvl` names a level Word does not have
             *   CONSEQUENCE
             *     no label; the paragraph takes part in no count, the same as an absent level.
             *
             * EXAMPLE
             *   LIST_LEVEL_COUNT = 9, so the only levels Word writes are 0 through 8
             *   w:ilvl w:val="9"   -> out of range -> null
             *   w:ilvl w:val="-1"  -> out of range -> null
             *
             * WHY
             *   `effectiveLevelsOf` always answers exactly LIST_LEVEL_COUNT entries, so an
             *   out-of-range index reads as `undefined` rather than `null`. It would pass the
             *   null guard below, reach `counters[reference.level]`, and throw there — and that
             *   throw escapes `labelsByParagraph`, so one malformed paragraph would cost every
             *   label in the document rather than its own.
             */
            if (reference.level < 0 || reference.level >= LIST_LEVEL_COUNT) return null;
            const currentLevel = levels[reference.level];
            if (currentLevel === null) return null;
            const counters = countersByInstance.get(reference.numberingId) ?? levels.map((level) => ({ value: level?.startValue ?? 0, used: false }));
            countersByInstance.set(reference.numberingId, counters);
            /**
             * WHEN the paragraph's mark is a tracked deletion
             *   CONSEQUENCE
             *     accepting the change merges it into the paragraph after it, so Word paints it with
             *     the count as it stands and does not advance; the next live paragraph takes the next
             *     number. A paragraph whose text is struck but whose mark is live still counts.
             */
            if (!OoxmlNumbering.paragraphMarkIsDeleted(paragraph)) OoxmlNumbering.tick(counters, levels, reference.level);
            const label = OoxmlNumbering.renderLabel(currentLevel.definition, counters, levels);
            /**
             * WHEN the rendered label is empty (`none`, or an empty pattern)
             *   CONSEQUENCE
             *     null: the paragraph is counted but shows nothing, and the agent's view carries no attribute for it.
             */
            return label === "" ? null : label;
        });
    }

    /**
     * The list level each of `paragraphs` is numbered at (`w:ilvl`, its own or
     * the one its paragraph style links it to), or null for a paragraph that
     * takes part in no list.
     *
     * WHY a caller wants this beside the label
     *   The label says how Word paints the number; the level says how deeply the
     *   paragraph nests. A heading map needs the depth, and reading it back out of
     *   a painted label ("17.6" -> two segments) guesses at what this tag states.
     *
     * EXAMPLE
     *   paragraphs at `w:ilvl` 0, 1, 1, 0 of one instance -> [0, 1, 1, 0]
     *
     * NULL
     *   The paragraph references no list, references one the numbering part does not
     *   define, or names a level outside the nine Word writes — the same cases
     *   `labelsByParagraph` gives no label to, so the two never disagree about
     *   whether a paragraph is numbered.
     */
    listLevelsByParagraph(paragraphs: readonly Element[]): (number | null)[] {
        return paragraphs.map((paragraph) => {
            const reference = this.numberingReferenceOf(paragraph);
            /**
             * WHEN the paragraph references no list, or one the numbering part does not define
             *   CONSEQUENCE
             *     no level; the paragraph is not numbered.
             */
            if (reference === null) return null;
            if (!this.instancesById.has(reference.numberingId)) return null;
            /**
             * WHEN `w:ilvl` names a level Word does not have
             *   CONSEQUENCE
             *     no level, matching `labelsByParagraph`, which gives such a paragraph no
             *     label and lets it take part in no count.
             *
             * EXAMPLE
             *   LIST_LEVEL_COUNT = 9, so the only levels Word writes are 0 through 8
             *   w:ilvl w:val="9"  -> out of range -> null
             *   w:ilvl w:val="-1" -> out of range -> null
             */
            if (reference.level < 0 || reference.level >= LIST_LEVEL_COUNT) return null;
            return reference.level;
        });
    }

    /**
     * Whether the paragraph mark is a tracked deletion (`w:pPr/w:rPr/w:del`),
     * which is how Word records a whole paragraph deleted with tracking on.
     */
    private static paragraphMarkIsDeleted(paragraph: Element): boolean {
        const paragraphProperties = firstElementChild(paragraph, "pPr");
        const markProperties = paragraphProperties === null ? null : firstElementChild(paragraphProperties, "rPr");
        return markProperties !== null && firstElementChild(markProperties, "del") !== null;
    }

    /**
     * Uses `usedLevel` of an instance once: its counter shows the start value
     * on first use and one more on each later use, and every deeper level that
     * restarts on it goes back to its start.
     *
     * EXAMPLE
     *   counters (0: 2 used, 1: 3 used), usedLevel 0, level 1 restarts by default
     *   -> counters (0: 3 used, 1: start(1) unused)
     */
    private static tick(counters: LevelCounter[], levels: readonly (EffectiveLevel | null)[], usedLevel: number): void {
        const counter = counters[usedLevel];
        /**
         * CASES
         *   - first use since the start or the last restart -> the counter already holds the start value; mark it used
         *   - a later use                                    -> one more
         */
        if (counter.used) counter.value += 1;
        else counter.used = true;
        for (let deeperLevel = usedLevel + 1; deeperLevel < LIST_LEVEL_COUNT; deeperLevel += 1) {
            const deeper = levels[deeperLevel];
            /**
             * WHEN the deeper level is not defined
             *   CONSEQUENCE
             *     nothing to restart.
             */
            if (deeper === null) continue;
            if (OoxmlNumbering.restartsOnUseOf(deeper.definition, usedLevel)) counters[deeperLevel] = { value: deeper.startValue, used: false };
        }
    }

    /**
     * Whether `level` goes back to its start when a paragraph at `usedLevel`
     * (shallower than it) is counted.
     *
     * CASES, by `w:lvlRestart`
     *   - omitted -> yes: any shallower level restarts it
     *   - 0       -> never
     *   - n       -> when the used level's one-based index is n or less
     *
     * EXAMPLE
     *   lvlRestart 1 on level 2 (ilvl 2), usedLevel 0 -> index 1 <= 1 -> restarts
     *   lvlRestart 1 on level 2 (ilvl 2), usedLevel 1 -> index 2 <= 1 is false -> keeps counting
     */
    private static restartsOnUseOf(level: ListLevelDefinition, usedLevel: number): boolean {
        if (level.restartsAfterLevel === null) return true;
        if (level.restartsAfterLevel === 0) return false;
        return usedLevel + 1 <= level.restartsAfterLevel;
    }

    /**
     * The label of the current level: the level text with each `%N` replaced
     * by the count of level N rendered in level N's own format, or in decimal
     * when the current level is legal.
     *
     * CASES
     *   - bullet or none -> the level text itself is the label
     *   - a placeholder for an undefined level -> rendered decimal from its counter
     *
     * EXAMPLE
     *   level text "%1.%2", counters (0: 17, 1: 6), formats decimal -> "17.6"
     *   level text "(%2)", counters (1: 2), format lowerLetter        -> "(b)"
     */
    private static renderLabel(definition: ListLevelDefinition, counters: readonly LevelCounter[], levels: readonly (EffectiveLevel | null)[]): string {
        if (isLiteralNumberFormat(definition.numberFormat)) return definition.levelText;
        return definition.levelText.replace(LEVEL_PLACEHOLDER, (_placeholder, levelDigit: string) => {
            const placeholderLevel = Number(levelDigit) - 1;
            const numberFormat = definition.isLegal ? "decimal" : (levels[placeholderLevel]?.definition.numberFormat ?? "decimal");
            return formatListNumber(counters[placeholderLevel].value, numberFormat, "");
        });
    }

    /**
     * The nine levels of an instance as they apply to it: the abstract
     * definition's levels, each replaced or restarted by the instance's
     * overrides.
     */
    private effectiveLevelsOf(instance: NumberingInstance): (EffectiveLevel | null)[] {
        const abstractDefinition = this.abstractDefinitionFor(instance);
        return Array.from({ length: LIST_LEVEL_COUNT }, (_unused, levelIndex) => {
            const override = instance.overridesByLevel.get(levelIndex);
            const definition = override?.level ?? abstractDefinition?.levelsByIndex.get(levelIndex) ?? null;
            if (definition === null) return null;
            return { definition, startValue: override?.startOverride ?? definition.startValue };
        });
    }

    /**
     * The abstract definition an instance counts by, following a
     * `w:numStyleLink` to the numbering style's own instance when the linked
     * definition is a stub.
     *
     * EXAMPLE
     *   num 5 -> abstractNum 3 (numStyleLink "LegalList") -> style LegalList numPr numId 2 -> num 2 -> abstractNum 1: the levels
     */
    private abstractDefinitionFor(instance: NumberingInstance): AbstractNumberingDefinition | null {
        let current = this.abstractDefinitionsById.get(instance.abstractNumberingId) ?? null;
        for (let depth = 0; current !== null && current.numberingStyleLink !== null && depth < MAX_LINK_DEPTH; depth += 1) {
            const linkedStyle = this.stylesById.get(current.numberingStyleLink);
            const linkedInstance = linkedStyle?.numberingId === null || linkedStyle === undefined ? undefined : this.instancesById.get(linkedStyle.numberingId);
            /**
             * WHEN the link points nowhere
             *   CONSEQUENCE
             *     the stub's own (usually empty) levels are all there is.
             */
            if (linkedInstance === undefined) break;
            current = this.abstractDefinitionsById.get(linkedInstance.abstractNumberingId) ?? null;
        }
        return current;
    }

    /**
     * Which instance counts a paragraph, at which level.
     *
     * CASES
     *   - `w:pPr/w:numPr` with a `w:numId`   -> that instance; the paragraph's `w:ilvl`, else the level its style links to, else 0
     *   - `w:numId` 0                        -> not numbered, whatever the style says
     *   - no `w:numId` of its own            -> the first style in the `w:basedOn` chain that names an instance
     *   - nothing names an instance          -> not numbered
     *
     * Only the paragraph's own properties block is read: a `w:pPrChange`
     * holds the properties before a tracked change, not the current ones.
     */
    private numberingReferenceOf(paragraph: Element): NumberingReference | null {
        const paragraphProperties = firstElementChild(paragraph, "pPr");
        const numberingProperties = paragraphProperties === null ? null : firstElementChild(paragraphProperties, "numPr");
        const ownNumberingId = numberingProperties === null ? null : attributeValue(firstElementChild(numberingProperties, "numId"), "val");
        const ownLevel = numberingProperties === null ? null : integerChildValue(numberingProperties, "ilvl");
        const styleId = paragraphProperties === null ? null : attributeValue(firstElementChild(paragraphProperties, "pStyle"), "val");
        if (ownNumberingId !== null) {
            if (ownNumberingId === "0") return null;
            return { numberingId: ownNumberingId, level: ownLevel ?? this.levelLinkedToStyle(ownNumberingId, styleId) ?? 0 };
        }
        for (let currentStyleId = styleId, depth = 0; currentStyleId !== null && depth < MAX_LINK_DEPTH; depth += 1) {
            const style = this.stylesById.get(currentStyleId);
            if (style === undefined) return null;
            if (style.numberingId !== null) {
                if (style.numberingId === "0") return null;
                return { numberingId: style.numberingId, level: ownLevel ?? style.level ?? this.levelLinkedToStyle(style.numberingId, styleId) ?? 0 };
            }
            currentStyleId = style.basedOnStyleId;
        }
        return null;
    }

    /**
     * The level of an instance whose definition names `styleId` as its
     * paragraph style, which is how heading styles are numbered: the style
     * says only which list, and the list says which level the style is.
     */
    private levelLinkedToStyle(numberingId: string, styleId: string | null): number | null {
        if (styleId === null) return null;
        const instance = this.instancesById.get(numberingId);
        if (instance === undefined) return null;
        const levels = this.effectiveLevelsOf(instance);
        const linkedIndex = levels.findIndex((level) => level !== null && level.definition.linkedParagraphStyleId === styleId);
        return linkedIndex === -1 ? null : linkedIndex;
    }

    private static abstractDefinitionsOf(numberingDocument: Document): Map<string, AbstractNumberingDefinition> {
        const definitions = new Map<string, AbstractNumberingDefinition>();
        for (const abstractElement of Array.from(numberingDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, "abstractNum"))) {
            const abstractId = attributeValue(abstractElement, "abstractNumId");
            /**
             * WHEN a definition carries no id
             *   CONSEQUENCE
             *     no instance can point at it; skip it.
             */
            if (abstractId === null) continue;
            const levelsByIndex = new Map<number, ListLevelDefinition>();
            for (const levelElement of elementChildren(abstractElement, "lvl")) {
                const levelIndex = Number.parseInt(attributeValue(levelElement, "ilvl") ?? "", 10);
                if (Number.isNaN(levelIndex)) continue;
                levelsByIndex.set(levelIndex, OoxmlNumbering.levelDefinitionOf(levelElement));
            }
            definitions.set(abstractId, { levelsByIndex, numberingStyleLink: attributeValue(firstElementChild(abstractElement, "numStyleLink"), "val") });
        }
        return definitions;
    }

    private static instancesOf(numberingDocument: Document): Map<string, NumberingInstance> {
        const instances = new Map<string, NumberingInstance>();
        for (const instanceElement of Array.from(numberingDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, "num"))) {
            const numberingId = attributeValue(instanceElement, "numId");
            const abstractNumberingId = attributeValue(firstElementChild(instanceElement, "abstractNumId"), "val");
            /**
             * WHEN an instance lacks its id or its definition
             *   CONSEQUENCE
             *     nothing can be counted by it; skip it.
             */
            if (numberingId === null || abstractNumberingId === null) continue;
            const overridesByLevel = new Map<number, ListLevelOverride>();
            for (const overrideElement of elementChildren(instanceElement, "lvlOverride")) {
                const levelIndex = Number.parseInt(attributeValue(overrideElement, "ilvl") ?? "", 10);
                if (Number.isNaN(levelIndex)) continue;
                const overriddenLevel = firstElementChild(overrideElement, "lvl");
                overridesByLevel.set(levelIndex, {
                    startOverride: integerChildValue(overrideElement, "startOverride"),
                    level: overriddenLevel === null ? null : OoxmlNumbering.levelDefinitionOf(overriddenLevel),
                });
            }
            instances.set(numberingId, { abstractNumberingId, overridesByLevel });
        }
        return instances;
    }

    /**
     * One `w:lvl` read into a definition, with the schema's defaults for what
     * the file omits.
     *
     * CASES, for `w:lvlText`
     *   - `w:val` given          -> the pattern
     *   - `w:null` or no element -> empty pattern
     */
    private static levelDefinitionOf(levelElement: Element): ListLevelDefinition {
        const levelTextElement = firstElementChild(levelElement, "lvlText");
        const levelTextIsNull = attributeValue(levelTextElement, "null") !== null && attributeValue(levelTextElement, "null") !== "0" && attributeValue(levelTextElement, "null") !== "false";
        return {
            startValue: integerChildValue(levelElement, "start") ?? 0,
            numberFormat: attributeValue(firstElementChild(levelElement, "numFmt"), "val") ?? "decimal",
            levelText: levelTextIsNull ? "" : (attributeValue(levelTextElement, "val") ?? ""),
            restartsAfterLevel: integerChildValue(levelElement, "lvlRestart"),
            isLegal: firstElementChild(levelElement, "isLgl") !== null && attributeValue(firstElementChild(levelElement, "isLgl"), "val") !== "0" && attributeValue(firstElementChild(levelElement, "isLgl"), "val") !== "false",
            linkedParagraphStyleId: attributeValue(firstElementChild(levelElement, "pStyle"), "val"),
        };
    }

    /**
     * Every `w:style` of the styles part: its own numbering reference and the
     * style it is based on, keyed by style id. Paragraph styles carry the
     * inherited numbering of their paragraphs; numbering styles are the
     * targets of `w:numStyleLink`.
     */
    private static stylesOf(stylesDocument: Document): Map<string, StyleNumbering> {
        const styles = new Map<string, StyleNumbering>();
        for (const styleElement of Array.from(stylesDocument.getElementsByTagNameNS(WORDPROCESSINGML_NS, "style"))) {
            const styleId = attributeValue(styleElement, "styleId");
            if (styleId === null) continue;
            const paragraphProperties = firstElementChild(styleElement, "pPr");
            const numberingProperties = paragraphProperties === null ? null : firstElementChild(paragraphProperties, "numPr");
            styles.set(styleId, {
                numberingId: numberingProperties === null ? null : attributeValue(firstElementChild(numberingProperties, "numId"), "val"),
                level: numberingProperties === null ? null : integerChildValue(numberingProperties, "ilvl"),
                basedOnStyleId: attributeValue(firstElementChild(styleElement, "basedOn"), "val"),
            });
        }
        return styles;
    }
}
